import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LandingBootLoader } from './LandingPage';
import Dashboard from './LiveDashboard';
import SupheroChatWidget from './SupheroChatWidget';
import TivigoAuthFrame from './TivigoAuthFrame';
import TivigoSeo from './TivigoSeo';
import TestRegistrationClosed from './TestRegistrationClosed';
import PublicSite, { isBlogPath } from './PublicSite';
// import ConsoleProtection from './bilesenler/ConsoleProtection'; // Geçici kapalı (tasarım için)
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { GoogleOAuthProvider } from '@react-oauth/google';

// XtreamAPI'yi import et ve global yap
import XtreamAPI from './services/XtreamAPI';
import { cacheManager } from './utils/CacheManager';
import { API_CONFIG } from './config/constants';
import { getRecaptchaLoginToken } from './utils/recaptchaLoginToken';
import {
  canAccountUseSupheroChat,
  getAccountAccessState,
  isRestrictedAccountReason,
} from './accountAccess';
import { requestTestAvailability } from './services/trialRegistration';
// SADECE GELISTIRME onizlemesi; uretimde route hic tanimlanmaz.
const DevAccountModalPreview = React.lazy(() => import('./DevAccountModalPreview'));

const LoginForm = React.lazy(() => import('./legacy/LoginForm'));
const SifremiUnuttum = React.lazy(() => import('./legacy/ForgotPassword'));
const KayitOl = React.lazy(() => import('./legacy/Register'));
const Kayit = React.lazy(() => import('./legacy/ReferralRegister'));
const OdemeIframe = React.lazy(() => import('./legacy/PaymentIframe'));

// Global erişim için
window.XtreamAPI = XtreamAPI;

const theme = createTheme({
  palette: {
    background: {
      default: '#f5f5f5'
    }
  }
});

const GOOGLE_CLIENT_ID = "12951649915-so6osp1sbqbdrr8g16kp4f071fmiod52.apps.googleusercontent.com";
const TEST_AVAILABILITY_REFRESH_MS = 15000;
const mergeAuthenticatedUserData = (current, data) => {
  const user = data?.user || {};
  const onlineStatus = data?.online_status || {};
  const access = getAccountAccessState({ user_info: user });

  return {
    ...(current || {}),
    user,
    user_info: {
      ...(current?.user_info || {}),
      ...user,
      auth: 1,
      status: access.statusCode,
      active_cons: onlineStatus.active_connections ?? current?.user_info?.active_cons ?? 0,
    },
    server_info: {
      url: 'https://tivigo.store',
      port: '80',
      server_protocol: 'https',
      ...(current?.server_info || {}),
      ...(data?.server_info || {}),
    },
    account_data: data || current?.account_data,
  };
};

function AppRoutes({ isLoggedIn, userInfo, onLoginSuccess, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [googleRegisterData, setGoogleRegisterData] = useState(null);
  const [testAvailability, setTestAvailability] = useState('loading');
  // Test hesabi suresi (saat). Sunucu ayarindan gelir; ulasilamazsa 3 saat varsayilir.
  const [trialHours, setTrialHours] = useState(3);
  const testAvailabilityRequestId = useRef(0);

  const refreshTestAvailability = useCallback(async () => {
    const requestId = ++testAvailabilityRequestId.current;

    try {
      const result = await requestTestAvailability();
      const enabled = result?.success === true && result?.data?.test_aktif === true;
      const hours = Number(result?.data?.test_suresi_saat);
      if (requestId === testAvailabilityRequestId.current) {
        setTestAvailability(enabled ? 'enabled' : 'closed');
        if ([1, 2, 3].includes(hours)) setTrialHours(hours);
      }
      return enabled;
    } catch (error) {
      if (requestId === testAvailabilityRequestId.current) {
        setTestAvailability('closed');
      }
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) refreshTestAvailability();
  }, [isLoggedIn, refreshTestAvailability]);

  useEffect(() => {
    if (isLoggedIn) return undefined;

    const refreshOnFocus = () => refreshTestAvailability();
    const refreshOnVisibility = () => {
      if (document.visibilityState === 'visible') refreshTestAvailability();
    };

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnVisibility);

    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnVisibility);
    };
  }, [isLoggedIn, refreshTestAvailability]);

  useEffect(() => {
    if (isLoggedIn || location.pathname !== '/test-kayit') return undefined;
    const timer = window.setInterval(refreshTestAvailability, TEST_AVAILABILITY_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [isLoggedIn, location.pathname, refreshTestAvailability]);

  const handleShowForgotPassword = () => navigate('/forgot-password');

  const handleBackToLogin = () => {
    setGoogleRegisterData(null);
    navigate('/login');
  };

  const handleLandingNavigate = (route) => {
    setGoogleRegisterData(null);
    navigate(route);
  };

  const handleShowRegister = (googleData, type = 'normal') => {
    if ((googleData || type === 'trial') && testAvailability !== 'enabled') return;
    setGoogleRegisterData(googleData);
    navigate(type === 'trial' ? '/test-kayit' : '/register');
  };

  // Ana sayfa ve blog tek yapı (PublicSite): Routes'un dışında, sabit konumda durur; böylece ikisi arasında
  // geçerken ana sayfa yok edilip yeniden çizilmez. Başka bir yola geçilince PublicSite kalkar.
  const onPublicSite = location.pathname === '/' || isBlogPath(location.pathname);

  return (
    <React.Suspense fallback={<LandingBootLoader />}>
      {onPublicSite && !(isLoggedIn && location.pathname === '/') && (
        <PublicSite isLoggedIn={isLoggedIn} onNavigate={handleLandingNavigate} />
      )}
      <Routes>
        <Route
          path="/"
          element={isLoggedIn ? <Navigate to="/dashboard" replace /> : null}
        />
        <Route path="/blog" element={null} />
        <Route path="/blog/sayfa/:page" element={null} />
        <Route path="/blog/:slug" element={null} />

        <Route
          path="/login"
          element={
            isLoggedIn ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <TivigoAuthFrame view="login" pageKey="login">
                <LoginForm
                  onLoginSuccess={onLoginSuccess}
                  onForgotPassword={handleShowForgotPassword}
                  onRegister={handleShowRegister}
                  onBackToLanding={() => navigate('/')}
                  testAvailability={testAvailability}
                  trialHours={trialHours}
                />
              </TivigoAuthFrame>
            )
          }
        />

        <Route
          path="/register"
          element={
            isLoggedIn ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <TivigoAuthFrame view="register" pageKey="register">
                <KayitOl
                  onBackToLogin={handleBackToLogin}
                  onBackToLanding={() => handleLandingNavigate('/')}
                  googleData={googleRegisterData}
                  registerType="normal"
                />
              </TivigoAuthFrame>
            )
          }
        />

        <Route
          path="/test-kayit"
          element={
            isLoggedIn ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <TivigoAuthFrame view="register" pageKey="test-register">
                {testAvailability === 'enabled' ? (
                  <KayitOl
                    onBackToLogin={handleBackToLogin}
                    onBackToLanding={() => handleLandingNavigate('/')}
                    googleData={googleRegisterData}
                    registerType="trial"
                    ensureTestRegistrationEnabled={refreshTestAvailability}
                  />
                ) : (
                  <TestRegistrationClosed
                    loading={testAvailability === 'loading'}
                    onCreateAccount={() => handleLandingNavigate('/register')}
                    onLogin={handleBackToLogin}
                  />
                )}
              </TivigoAuthFrame>
            )
          }
        />

        <Route
          path="/forgot-password"
          element={
            isLoggedIn ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <TivigoAuthFrame view="forgot" pageKey="forgot-password">
                <SifremiUnuttum onBackToLogin={handleBackToLogin} />
              </TivigoAuthFrame>
            )
          }
        />

        <Route
          path="/kayit"
          element={
            isLoggedIn ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <TivigoAuthFrame view="register" pageKey="referral-register">
                <Kayit onBackToLogin={handleBackToLogin} />
              </TivigoAuthFrame>
            )
          }
        />

        <Route path="/giris" element={<Navigate to="/login" replace />} />
        <Route path="/kayit-ol" element={<Navigate to="/register" replace />} />
        <Route path="/sifremi-unuttum" element={<Navigate to="/forgot-password" replace />} />

        <Route path="/odeme-iframe" element={<OdemeIframe />} />

        {/* SADECE GELISTIRME: hesap modali onizlemesi. Uretim derlemesinde bu yol yoktur. */}
        {process.env.NODE_ENV === 'development' ? (
          <Route path="/dev-hesap-onizleme" element={<DevAccountModalPreview />} />
        ) : null}

        <Route
          path="/dashboard/*"
          element={
            isLoggedIn ? (
              <Dashboard userInfo={userInfo} onLogout={onLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // ✅ SMART PERSISTENT SESSION - Retry Limit
  const [tokenRetryCount, setTokenRetryCount] = useState(0);
  const MAX_TOKEN_RETRY = 3;

  // ✅ PERIODIC BACKEND VALIDATION - Her 5 dakikada user durumu kontrol et
  useEffect(() => {
    if (!isLoggedIn) return;
    
    const validateSession = async () => {
      try {
        const credentials = JSON.parse(localStorage.getItem('xtream_credentials') || '{}');
        if (!credentials.username || !credentials.password) return;
        
        console.log('🔍 Periodic session validation...');
        
        const validation = await XtreamAPI.validateUserStatus();
        
        if (!validation.valid) {
          console.error('❌ Session validation failed:', validation.reason);

          if (isRestrictedAccountReason(validation.reason)) {
            if (validation.user?.user) {
              setUserInfo((current) => {
                const next = mergeAuthenticatedUserData(current, validation.user);
                localStorage.setItem('xtream_user', JSON.stringify(next));
                return next;
              });
            }
            console.log('🔒 Kısıtlı hesap oturumu yenileme işlemleri için açık tutuluyor.');
          }
          // Diğer hatalar için sadece log (network error, vs.)
        } else {
          console.log('✅ Session validation OK');
        }
        
      } catch (error) {
        console.error('Session validation error:', error);
        // Network hatalarında LOGOUT YAPMA! Sadece log
      }
    };
    
    // ✅ İlk kontrolü daha geç yap (30 saniye sonra) - Login tamamlansın diye
    const initialTimeout = setTimeout(validateSession, 30000);
    
    // Her 5 dakikada bir kontrol et
    const interval = setInterval(validateSession, 5 * 60 * 1000);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    const initializeApp = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');
      
      if (refCode) {
        // Referans kodunu gerçek kayıt rotasına taşı
        window.history.replaceState({}, '', `/kayit?ref=${encodeURIComponent(refCode)}`);
        setLoading(false);
        return;
      }
      
      const quickCode = urlParams.get('q');
      if (quickCode && quickCode.length === 8) {
        try {
          const rcQuick = await getRecaptchaLoginToken();
          if (!rcQuick) {
            console.error('Quick login: reCAPTCHA token alınamadı');
            window.history.replaceState({}, '', '/');
          } else {
          const qResponse = await fetch(
            `${API_CONFIG.BASE_URL}?endpoint=verify_quick_login&code=${encodeURIComponent(quickCode)}&login_client_key=${encodeURIComponent(API_CONFIG.LOGIN_CLIENT_KEY)}&recaptcha_token=${encodeURIComponent(rcQuick)}`
          );
          const qResult = await qResponse.json();
          
          if (qResult.success && qResult.data?.token) {
            localStorage.setItem('current_username', qResult.data.user.username);
            localStorage.setItem('xtream_credentials', JSON.stringify({
              username: qResult.data.user.username,
              password: qResult.data.user.password || ''
            }));
            localStorage.setItem('xtream_api_token', qResult.data.token);
            
            const userResponse = await fetch(`${API_CONFIG.BASE_URL}?endpoint=user_data&token=${qResult.data.token}`);
            const userData = await userResponse.json();
            
            if (userData.success) {
              const compatibleUserData = mergeAuthenticatedUserData(null, userData.data);
              
              localStorage.setItem('xtream_user', JSON.stringify(compatibleUserData));
              window.history.replaceState({}, '', '/');
              setUserInfo(compatibleUserData);
              setIsLoggedIn(true);
              setLoading(false);
              return;
            }
          }
          }
        } catch (err) {
          console.error('Quick login error:', err);
        }
        window.history.replaceState({}, '', '/');
      }
      
      const savedUser = localStorage.getItem('xtream_user');
      const savedCredentials = localStorage.getItem('xtream_credentials');
      const savedToken = localStorage.getItem('xtream_api_token');
      
      // ✅ ÖNCE TOKEN KONTROLÜ - Eski token'ları hemen reddet
      if (savedToken) {
        try {
          const validationResponse = await fetch(
            `https://tivigo.store/endpoint.php?endpoint=user_data&token=${savedToken}`
          );
          const validationData = await validationResponse.json();
          
          // Token geçersizse (401, 403 veya başarısız) - ESKİ TOKEN REDDEDİLDİ
          if (!validationData.success || validationResponse.status === 401 || validationResponse.status === 403) {
            console.log('❌ Eski token reddedildi, logout yapılıyor...');
            forceLogout('Oturum sonlandırıldı. Lütfen yeniden giriş yapın.');
            setLoading(false);
            return; // State set etmeden çık
          }
        } catch (error) {
          // Network hatası değilse (401, 403 gibi) - ESKİ TOKEN REDDEDİLDİ
          if (error.message.includes('401') || error.message.includes('403') || error.message.includes('Geçersiz')) {
            console.log('❌ Eski token reddedildi, logout yapılıyor...');
            forceLogout('Oturum sonlandırıldı. Lütfen yeniden giriş yapın.');
            setLoading(false);
            return; // State set etmeden çık
          }
        }
      }
      
      if (savedUser && savedCredentials) {
        try {
          const userData = JSON.parse(savedUser);
          const credentials = JSON.parse(savedCredentials);
          
          if (userData.user_info && userData.user_info.auth === 1) {
            // ✅ Önce state'i set et (hemen giriş yap)
            setUserInfo(userData);
            setIsLoggedIn(true);
            
            // ✅ Arka planda token + user validation yap
            checkAndRefreshUserData(credentials);
          }
        } catch (error) {
          console.error('Saved user parse error:', error);
          // Hatalı veri varsa temizle
          localStorage.removeItem('xtream_user');
          localStorage.removeItem('xtream_credentials');
          localStorage.removeItem('xtream_api_token');
          localStorage.removeItem('xtream_token_expires');
        }
      }
      
      setLoading(false);
    };
    
    initializeApp();
  }, []);


  // ✅ SMART TOKEN REFRESH - Backend Validation ile
  const checkAndRefreshUserData = async (credentials) => {
    try {
      const token = localStorage.getItem('xtream_api_token');
      
      if (!token) {
        // Token yok, credentials ile login dene
        await smartRefreshToken(credentials);
        return;
      }
      
      // ✅ ESKİ TOKEN KONTROLÜ - Backend'de token'ı validate et
      // Eski token'ları reddetmek için backend'e istek yap
      try {
        const validationResponse = await fetch(
          `https://tivigo.store/endpoint.php?endpoint=user_data&token=${token}`
        );
        const validationData = await validationResponse.json();
        
        // Token geçersizse (401, 403 veya başarısız) - ESKİ TOKEN REDDEDİLDİ
        if (!validationData.success || validationResponse.status === 401 || validationResponse.status === 403) {
          console.log('❌ Eski token reddedildi, logout yapılıyor...', {
            success: validationData.success,
            status: validationResponse.status,
            message: validationData.message
          });
          // ✅ Otomatik login yapma, direkt logout yap
          forceLogout('Oturum sonlandırıldı. Lütfen yeniden giriş yapın.');
          return;
        }

        if (validationData.data?.user?.id != null) {
          setUserInfo((prev) => {
            const next = mergeAuthenticatedUserData(prev, validationData.data);
            localStorage.setItem('xtream_user', JSON.stringify(next));
            return next;
          });
        }
      } catch (error) {
        // Network hatası değilse (401, 403 gibi) - ESKİ TOKEN REDDEDİLDİ
        console.log('❌ Token validation error:', error);
        if (error.message.includes('401') || error.message.includes('403') || error.message.includes('Geçersiz')) {
          console.log('❌ Eski token reddedildi, logout yapılıyor...');
          // ✅ Otomatik login yapma, direkt logout yap
          forceLogout('Oturum sonlandırıldı. Lütfen yeniden giriş yapın.');
          return;
        }
      }
      
      const tokenStatus = XtreamAPI.getTokenStatus();
      
      // Token expire olmuş veya yakında dolacak
      if (tokenStatus.isExpired || tokenStatus.isExpiringSoon) {
        console.log('🔄 Token yenilenecek:', tokenStatus);
        await smartRefreshToken(credentials);
      }
      
    } catch (error) {
      console.error('Token refresh kontrolü hatası:', error);
    }
  };

  // ✅ SMART TOKEN REFRESH - Validation + Retry Limit
  const smartRefreshToken = async (credentials) => {
    try {
      // Retry limit kontrolü
      if (tokenRetryCount >= MAX_TOKEN_RETRY) {
        console.error('❌ Token yenileme limiti aşıldı');
        forceLogout('Oturum sürekli geçersiz, lütfen tekrar giriş yapın');
        return false;
      }
      
      setTokenRetryCount(prev => prev + 1);
      
      const rc = await getRecaptchaLoginToken();
      if (!rc) {
        console.error('reCAPTCHA token alınamadı');
        forceLogout('Güvenlik doğrulaması gerekli. Lütfen tekrar giriş yapın.');
        return false;
      }
      const response = await fetch(
        `${API_CONFIG.BASE_URL}?endpoint=login&username=${encodeURIComponent(credentials.username)}&password=${encodeURIComponent(credentials.password)}&login_client_key=${encodeURIComponent(API_CONFIG.LOGIN_CLIENT_KEY)}&recaptcha_token=${encodeURIComponent(rc)}`
      );
      const loginResult = await response.json();
      
      if (!loginResult.success) {
        console.error('❌ Login başarısız:', loginResult.message);
        forceLogout('Oturum bilgileri geçersiz');
        return false;
      }
      
      // Token'ı kaydet (EXPIRE ZAMANI İLE BİRLİKTE!)
      const token = loginResult.data.token;
      const expiresIn = loginResult.data.expires_in || 86400; // Default 24 saat
      
      localStorage.setItem('xtream_api_token', token);
      const expiryTime = Date.now() + (expiresIn * 1000);
      localStorage.setItem('xtream_token_expires', expiryTime.toString());
      
      // ✅ Backend'den user data al ve validate et
      const validation = await XtreamAPI.validateUserStatus();
      
      if (!validation.valid && !isRestrictedAccountReason(validation.reason)) {
        console.error('❌ User validation failed:', validation.reason);
        forceLogout(validation.message || 'Hesap durumu geçersiz');
        return false;
      }

      if (!validation.user?.user) {
        forceLogout('Hesap bilgileri alınamadı');
        return false;
      }
      
      const compatibleUserData = mergeAuthenticatedUserData(null, validation.user);
      
      localStorage.setItem('xtream_user', JSON.stringify(compatibleUserData));
      setUserInfo(compatibleUserData);
      setIsLoggedIn(true);
      
      // Retry counter'ı sıfırla (başarılı refresh)
      setTokenRetryCount(0);
      
      console.log('✅ Token başarıyla yenilendi ve validate edildi');
      return true;
      
    } catch (error) {
      console.error('Smart refresh hatası:', error);
      
      // Network hatası değilse force logout
      if (error.response?.status === 401 || error.response?.status === 403) {
        forceLogout('Oturum geçersiz');
      }
      
      return false;
    }
  };

  const handleLogin = async (credentials) => {
    try {
      const rc = await getRecaptchaLoginToken();
      if (!rc) return;
      const response = await fetch(
        `${API_CONFIG.BASE_URL}?endpoint=login&username=${encodeURIComponent(credentials.username)}&password=${encodeURIComponent(credentials.password)}&login_client_key=${encodeURIComponent(API_CONFIG.LOGIN_CLIENT_KEY)}&recaptcha_token=${encodeURIComponent(rc)}`
      );
      const loginResult = await response.json();
      
      if (loginResult.success) {
        // ✅ Önceki kullanıcı farklıysa TÜM cache'i temizle
        const previousUsername = localStorage.getItem('current_username');
        if (previousUsername && previousUsername !== credentials.username) {
          console.log(`🔄 Kullanıcı değişti: ${previousUsername} → ${credentials.username}`);
          
          // Eski kullanıcının tüm cache'lerini temizle
          const count = cacheManager.clearUserCache(previousUsername);
          
          // Legacy cache'leri de temizle
          localStorage.removeItem('recently_watched_channels');
          localStorage.removeItem('webtv_channels_cache');
          localStorage.removeItem('webtv_channels_timestamp');
          
          // Eski sistem cache'leri (vod, series)
          const keysToRemove = Object.keys(localStorage).filter(key => 
            key.startsWith('vod_') || 
            key.startsWith('series_') || 
            key.startsWith('webtv_') ||
            key.startsWith('populer_')
          );
          keysToRemove.forEach(key => localStorage.removeItem(key));
          
          console.log(`✅ Toplam ${count + keysToRemove.length} cache temizlendi`);
        }
        
        // Şu anki kullanıcı adını kaydet
        localStorage.setItem('current_username', credentials.username);
        localStorage.setItem('xtream_credentials', JSON.stringify(credentials));
        
        // ✅ Token + Expire Time birlikte kaydet
        const token = loginResult.data.token;
        const expiresIn = loginResult.data.expires_in || 86400; // Default 24 saat
        localStorage.setItem('xtream_api_token', token);
        const expiryTime = Date.now() + (expiresIn * 1000);
        localStorage.setItem('xtream_token_expires', expiryTime.toString());
        
        // Retry counter'ı sıfırla
        setTokenRetryCount(0);
        
        const userResponse = await fetch(
          `https://tivigo.store/endpoint.php?endpoint=user_data&token=${token}`
        );
        const userData = await userResponse.json();
        
        if (userData.success) {
          const compatibleUserData = mergeAuthenticatedUserData(null, userData.data);
          
          localStorage.setItem('xtream_user', JSON.stringify(compatibleUserData));
          setUserInfo(compatibleUserData);
          setIsLoggedIn(true);
          
          // DNS güncelleme kontrolü
          checkDNSUpdates(token);
        }
      }
    } catch (error) {
    }
  };

  const checkDNSUpdates = async (token) => {
    try {
      const response = await fetch(
        `https://tivigo.store/endpoint.php?endpoint=check_dns_updates&token=${token}`
      );
      const result = await response.json();
      
      if (result.success && result.data.has_dns_updates) {
        // DNS güncelleme var, bildirim sistemi otomatik olarak bildirim gönderecek
      }
    } catch (error) {
      console.error('DNS güncelleme kontrolü hatası:', error);
    }
  };

  const handleLoginSuccess = (userData) => {
    setUserInfo(userData);
    setIsLoggedIn(true);
    
  };

  // ✅ FORCE LOGOUT - Ghost Session Önleme
  const forceLogout = (reason = 'Oturum sonlandırıldı') => {
    console.log(`🚪 Force Logout: ${reason}`);
    
    const username = cacheManager.getCurrentUsername();
    
    // Kullanıcı cache'lerini temizle
    cacheManager.clearUserCache(username);
    
    // Tüm Tivigo cache'lerini temizle (güvenlik için)
    cacheManager.clearAll();
    
    // Auth verileri - HEPSİNİ TEMİZLE
    localStorage.removeItem('xtream_user');
    localStorage.removeItem('xtream_credentials');
    localStorage.removeItem('xtream_api_token');
    localStorage.removeItem('xtream_token_expires');
    localStorage.removeItem('current_username');
    
    // Legacy cache'ler
    localStorage.removeItem('recently_watched_channels');
    localStorage.removeItem('webtv_channels_cache');
    localStorage.removeItem('webtv_channels_timestamp');
    
    // State temizle
    setIsLoggedIn(false);
    setUserInfo(null);
    setTokenRetryCount(0); // Retry counter'ı sıfırla
    
    console.log('✅ Force Logout: Tüm veriler temizlendi');
    
    // ✅ Sayfayı yenile - State güncellemesi garantili olsun
    window.location.href = '/';
  };

  const handleLogout = () => {
    // Manuel logout - forceLogout kullan
    forceLogout('Kullanıcı çıkış yaptı');
  };

  // Blog herkese açıktır ve oturum kontrolünü beklemez; yükleme ekranı gösterilirse statik (hazır) blog içeriği
  // bir an silinip koyu ekranla değişir. Oturum sonradan netleşince ilgili parçalar kendiliğinden güncellenir.
  const onBlogRoute = typeof window !== 'undefined' && /^\/blog(\/|$)/.test(window.location.pathname);

  if (loading && !onBlogRoute) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LandingBootLoader />
        </ThemeProvider>
      </GoogleOAuthProvider>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {canAccountUseSupheroChat(isLoggedIn, userInfo) && (
          <SupheroChatWidget isLoggedIn={isLoggedIn} userInfo={userInfo} />
        )}
        <BrowserRouter>
          <TivigoSeo userInfo={userInfo} />
          {/* <ConsoleProtection /> */}
          <AppRoutes
            isLoggedIn={isLoggedIn}
            userInfo={userInfo}
            onLoginSuccess={handleLoginSuccess}
            onLogout={handleLogout}
          />
        </BrowserRouter>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
